import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { apiGetMasterCode, apiGetDevice } from "../api/api";
import {
  createMqttClient,
  extractEvtCd,
  isEventTopic,
  MQTT_MAX_RECONNECT_ATTEMPTS,
  parseEventPayload,
  parseEventTopic,
} from "../mqtt/mqttClient";
import { isEmpty } from "../utils/comm";

const MqttContext = createContext(null);

const SUBSCRIBE_TOPIC = "iv/event/+/+";

function getEvtNm(evtCdData, evtCd) {
  if (isEmpty(evtCd) || !Array.isArray(evtCdData)) {
    return "";
  }

  const matched = evtCdData.find((item) => item.code === evtCd);
  return matched?.codeNm || evtCd;
}

export function MqttProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const clientRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [eventModalVisible, setEventModalVisible] = useState(false);
  const [eventInfo, setEventInfo] = useState({
    updateDt: "",
    equSn: "",
    equNo: "",
    evtNm: "",
  });
  const [lastEvent, setLastEvent] = useState(null);

  const closeEventModal = useCallback(() => {
    setEventModalVisible(false);
  }, []);

  const openEventPopup = useCallback(async (topic, payload) => {
    const topicInfo = parseEventTopic(topic);
    if (!topicInfo) {
      return;
    }

    const { siteId, devId } = topicInfo;
    const sessionSiteId = sessionStorage.getItem("siteId");

    if (!isEmpty(sessionSiteId) && String(siteId) !== String(sessionSiteId)) {
      return;
    }

    const evtCd = extractEvtCd(payload);
    if (isEmpty(evtCd)) {
      return;
    }

    setLastEvent({ topic, payload, receivedAt: new Date() });

    let evtCdData = [];
    let equSn = "";
    try {
      const masterResponse = await apiGetMasterCode({ catCd: "EVT" });
      if (masterResponse?.data?.code === 0) {
        evtCdData = masterResponse.data.data || [];
      }
      const deviceRes = await apiGetDevice({
        siteId,
        svcCdList: sessionStorage.getItem("svcCd") || "",
      });
      if (deviceRes?.data?.code === 0) {
        const device = deviceRes?.data?.data?.find(
          (d) => String(d.devId) === String(devId)
        );
        equSn = device?.equSn || "";
      }
    } catch (e) {
      console.error("[MQTT] failed to load event code", e);
    }

    setEventInfo({
      updateDt: moment().format("YYYY-MM-DD HH:mm:ss"),
      equSn: equSn,
      equNo: String(devId),
      evtNm: getEvtNm(evtCdData, evtCd),
    });
    setEventModalVisible(true);

    if (location.pathname !== "/home") {
      sessionStorage.setItem("tabTitle", "HOME");
      navigate("/home");
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    const userId = sessionStorage.getItem("userId");
    if (isEmpty(userId)) {
      return undefined;
    }

    const client = createMqttClient();
    if (!client) {
      return undefined;
    }

    clientRef.current = client;
    let reconnectAttempts = 0;

    client.on("connect", () => {
      reconnectAttempts = 0;
      console.log("[MQTT] connected");
      setIsConnected(true);
      client.subscribe(SUBSCRIBE_TOPIC, (err) => {
        if (err) {
          console.error("[MQTT] subscribe failed", err);
        }
      });
    });

    client.on("reconnect", () => {
      reconnectAttempts += 1;
      if (reconnectAttempts >= MQTT_MAX_RECONNECT_ATTEMPTS) {
        console.error("[MQTT] max reconnect attempts reached, stopping");
        client.options.reconnectPeriod = 0;
        client.end(true);
        clientRef.current = null;
        setIsConnected(false);
      }
    });

    client.on("message", (topic, message) => {
      if (!isEventTopic(topic)) {
        return;
      }

      const payload = parseEventPayload(message);
      openEventPopup(topic, payload);
    });

    client.on("close", () => {
      setIsConnected(false);
    });

    client.on("error", (err) => {
      console.error("[MQTT] connection error", err);
    });

    return () => {
      client.end(true);
      clientRef.current = null;
      setIsConnected(false);
    };
  }, [openEventPopup]);

  return (
    <MqttContext.Provider
      value={{
        isConnected,
        lastEvent,
        eventModalVisible,
        eventInfo,
        closeEventModal,
      }}
    >
      {children}
    </MqttContext.Provider>
  );
}

export function useMqtt() {
  return useContext(MqttContext);
}
